import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import type {
	AIAnalysis,
	AllocationDateEntry,
	Asset,
	BenchmarkSeriesEntry,
	Goal,
	GoalData,
	MarketNews,
	MetricsData,
	Portfolio,
	PortfolioData,
	PortfolioSeriesEntry,
	TaxInfo,
	Transaction,
} from "../core";
import { createPortfolioDataProvider, type DataSource } from "../data";
import { toUiErrorMessage } from "../utils/error";
import { client } from "../utils/supabase";

interface DataContextType {
	loading: boolean;
	error: string | null;
	source: DataSource | null;
	generatedAt: string | null;

	portfolioId: string;
	portfolio: Portfolio | null;
	privacyMode: boolean;

	transactions: Transaction[];
	assets: Asset[];
	goal: Goal | null;
	aiAnalysis: AIAnalysis | null;
	aiAnalysisHistory: AIAnalysis[];
	marketNews: MarketNews[];

	allocationHistory: AllocationDateEntry[];
	portfolioSeries: PortfolioSeriesEntry[];
	benchmarkSeries: BenchmarkSeriesEntry[];
	metrics: MetricsData;
	goalData: GoalData | null;
	taxInfo: TaxInfo;

	refetch: () => Promise<void>;
}

const DataContext = createContext<DataContextType | undefined>(undefined);

interface DataProviderProps {
	portfolioId: string;
	children: ReactNode;
}

export function DataProvider({ portfolioId, children }: DataProviderProps) {
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState<string | null>(null);
	const [source, setSource] = useState<DataSource | null>(null);
	const [data, setData] = useState<PortfolioData | null>(null);
	const fetchPromiseRef = useRef<Promise<void> | null>(null);

	const fetchData = useCallback(
		async (force = false) => {
			if (!force && fetchPromiseRef.current) {
				return fetchPromiseRef.current;
			}

			const executeFetch = async () => {
				try {
					setLoading(true);
					setError(null);
					setData(null);

					const provider = await createPortfolioDataProvider(client.getClient());
					setSource(provider.source);
					setData(await provider.load(portfolioId));
				} catch (err: unknown) {
					console.error("Error loading portfolio data:", err);
					setError(toUiErrorMessage(err));
				} finally {
					setLoading(false);
				}
			};

			const request = executeFetch();
			fetchPromiseRef.current = request;
			void request.finally(() => {
				if (fetchPromiseRef.current === request) {
					fetchPromiseRef.current = null;
				}
			});

			return request;
		},
		[portfolioId],
	);

	useEffect(() => {
		void fetchData(true);
	}, [fetchData]);

	const value = useMemo<DataContextType>(
		() => ({
			loading,
			error,
			source,
			generatedAt: data?.generatedAt ?? null,

			portfolioId,
			portfolio: data?.portfolio ?? null,
			privacyMode: data?.privacyMode ?? false,

			transactions: data?.transactions ?? [],
			assets: data?.assets ?? [],
			goal: data?.goal ?? null,
			aiAnalysis: data?.aiAnalysis ?? null,
			aiAnalysisHistory: data?.aiAnalysisHistory ?? [],
			marketNews: data?.marketNews ?? [],

			allocationHistory: data?.allocationHistory ?? [],
			portfolioSeries: data?.portfolioSeries ?? [],
			benchmarkSeries: data?.benchmarkSeries ?? [],
			metrics: data?.metrics ?? {
				hasBenchmark: false,
				historicalReturns: {
					Monthly: [],
					Quarterly: [],
					Annually: [],
				},
				risk: {
					portfolio: {
						maxDrawdown: 0,
						currentDrawdown: 0,
						maxDDStartDate: "",
						maxDDTroughDate: "",
						annualizedVolatility: 0,
						annualizedReturn: 0,
						sharpeRatio: 0,
						winRate: 0,
						bestDay: { date: "", return: 0 },
						worstDay: { date: "", return: 0 },
					},
					benchmark: {
						maxDrawdown: 0,
						currentDrawdown: 0,
						maxDDStartDate: "",
						maxDDTroughDate: "",
						annualizedVolatility: 0,
						annualizedReturn: 0,
						sharpeRatio: 0,
						winRate: 0,
						bestDay: { date: "", return: 0 },
						worstDay: { date: "", return: 0 },
					},
				},
			},
			goalData: data?.goalData ?? null,
			taxInfo: data?.taxInfo ?? {},

			refetch: () => fetchData(true),
		}),
		[loading, error, source, data, fetchData, portfolioId],
	);

	return <DataContext.Provider value={value}>{children}</DataContext.Provider>;
}

// eslint-disable-next-line react-refresh/only-export-components
export function useData() {
	const context = useContext(DataContext);
	if (!context) {
		throw new Error("useData must be used within a DataProvider");
	}
	return context;
}
